import React from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiTarget, FiBox, FiPieChart } from 'react-icons/fi';

const MacroCard = ({ label, grams, calories, pct, color, icon }) => (
  <div className="bg-gray-50 dark:bg-gray-900/50 p-5 rounded-2xl border border-gray-100 dark:border-gray-700/50">
    <div className="flex items-center justify-between mb-4">
      <div className={`w-10 h-10 rounded-xl ${color} bg-opacity-10 dark:bg-opacity-20 flex items-center justify-center`}>
        <SafeIcon icon={icon} className={color.replace('bg-', 'text-')} />
      </div>
      <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{pct}%</span>
    </div>
    <div className="space-y-1">
      <h4 className="text-sm font-bold text-gray-500 uppercase tracking-tight">{label}</h4>
      <div className="text-2xl font-black tracking-tighter dark:text-white">{grams}g</div>
      <div className="text-[10px] font-medium text-gray-400 uppercase tracking-widest">{calories} kcal</div>
    </div>
  </div>
);

const MacroBreakdown = ({ macros }) => {
  if (!macros) return null;

  return (
    <div className="space-y-8 mt-12 pt-12 border-t border-gray-50 dark:border-gray-800">
      <div className="flex items-center gap-3">
        <div className="w-1.5 h-6 bg-blue-600 rounded-full" />
        <h3 className="text-xl font-black tracking-tight uppercase">Nutrient Architecture</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MacroCard 
          label="Protein" 
          grams={macros.protein.grams} 
          calories={macros.protein.cal} 
          pct={macros.protein.pct}
          color="bg-blue-500"
          icon={FiTarget}
        />
        <MacroCard 
          label="Carbohydrates" 
          grams={macros.carbs.grams} 
          calories={macros.carbs.cal} 
          pct={macros.carbs.pct}
          color="bg-emerald-500"
          icon={FiBox}
        />
        <MacroCard 
          label="Healthy Fats" 
          grams={macros.fat.grams} 
          calories={macros.fat.cal} 
          pct={macros.fat.pct}
          color="bg-amber-500"
          icon={FiPieChart}
        />
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/10 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4 border border-blue-100 dark:border-blue-800/30">
        <div>
          <h4 className="font-bold text-blue-900 dark:text-blue-200">Per Meal Breakdown</h4>
          <p className="text-xs text-blue-700 dark:text-blue-400/70">Distributed across 4 standard meals per day</p>
        </div>
        <div className="flex gap-6">
          <div className="text-center">
            <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest">P</div>
            <div className="font-black text-blue-900 dark:text-blue-100">{macros.perMeal.p}g</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest">C</div>
            <div className="font-black text-blue-900 dark:text-blue-100">{macros.perMeal.c}g</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest">F</div>
            <div className="font-black text-blue-900 dark:text-blue-100">{macros.perMeal.f}g</div>
          </div>
          <div className="h-8 w-px bg-blue-200 dark:bg-blue-800" />
          <div className="text-center">
            <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Kcal</div>
            <div className="font-black text-blue-900 dark:text-blue-100">{macros.perMeal.cal}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MacroBreakdown;